import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Mail, Phone, Save, Upload, Eye, EyeOff, Settings, Crown, Shield,
  RefreshCw, CheckCircle, AlertCircle, Zap, MessageSquare, Image,
  ExternalLink, Copy, ChevronDown, ChevronUp, ToggleLeft, ToggleRight, Key,
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { resolveImage } from '../../utils/imageUrl';

// ── Helpers ────────────────────────────────────────────────────────────────────
const interpolate = (str = '', vars = {}) =>
  str.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);

const SAMPLE_VARS = {
  name:          'John Doe',
  inquiryNumber: 'HOS-2026-000123',
  email:         'john@example.com',
  phone:         '+91 98765 43210',
  message:       'I am interested in your sample catalogue solutions.',
  otp:           '847291',
};

const VARIABLES = ['name','inquiryNumber','email','phone','message','otp'];

const DEFAULT_TMPL = {
  smsHeader:      'Dear {{name}}, your House of Sample inquiry {{inquiryNumber}} has been received.',
  smsBody:        'Our team will contact you within 1 business day.',
  smsVisitUrl:    'https://www.houseofsample.in',
  smsWriteEmail:  'sales@houseofsample.in',
  smsReachPhone:  '+91 85114 28123',
  emailHeader:    'Thank You for Your Inquiry — {{inquiryNumber}}',
  emailBody:      'Dear {{name}},\n\nWe have received your inquiry and our team will get back to you within 1 business day.\n\nThank you for reaching out to House of Sample.',
  emailLogoUrl:   '',
  emailVisitUrl:  'https://www.houseofsample.in',
  emailWriteEmail:'sales@houseofsample.in',
  emailReachPhone:'+91 85114 28123',
};

// ── Sub-components ─────────────────────────────────────────────────────────────
function RoleBadge({ isMasterAdmin }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full
      ${isMasterAdmin ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
      {isMasterAdmin ? <Crown size={11} /> : <Shield size={11} />}
      {isMasterAdmin ? 'Master Admin' : 'Web Manager'}
    </span>
  );
}

function VarChips({ onInsert }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {VARIABLES.map(v => (
        <button key={v} type="button" onClick={() => onInsert(`{{${v}}}`)}
          className="text-xs px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full hover:bg-amber-100 transition-colors font-mono">
          {`{{${v}}}`}
        </button>
      ))}
    </div>
  );
}

function SectionCard({ title, icon: Icon, iconColor = '#B8A830', children, collapsible = false }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div
        className={`flex items-center justify-between px-5 py-4 border-b border-gray-100 ${collapsible ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `${iconColor}18` }}>
            <Icon size={15} style={{ color: iconColor }} />
          </div>
          <h3 className="font-semibold text-gray-800 text-sm">{title}</h3>
        </div>
        {collapsible && (open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />)}
      </div>
      {(!collapsible || open) && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{label}</label>
      {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-amber-400 transition-colors bg-white";
const textareaCls = `${inputCls} resize-none`;

function TextInput({ value, onChange, placeholder, disabled }) {
  return <input className={`${inputCls} ${disabled ? 'bg-gray-50 text-gray-400' : ''}`}
    value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} />;
}

function TextArea({ value, onChange, rows = 3, onInsert, disabled }) {
  const ref = useRef(null);
  const handleInsert = (token) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange({ target: { value: next } });
    setTimeout(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); }, 0);
  };
  return (
    <>
      {onInsert && <VarChips onInsert={handleInsert} />}
      <textarea ref={ref} className={`${textareaCls} ${disabled ? 'bg-gray-50 text-gray-400' : ''}`}
        rows={rows} value={value} onChange={onChange} disabled={disabled} />
    </>
  );
}

// ── Email Preview ──────────────────────────────────────────────────────────────
function EmailPreview({ tmpl, vars }) {
  const header = interpolate(tmpl.emailHeader, vars);
  const body   = interpolate(tmpl.emailBody, vars).replace(/\n/g, '<br/>');
  const logo   = tmpl.emailLogoUrl
    ? `<img src="${tmpl.emailLogoUrl}" alt="Logo" style="max-height:56px;margin-bottom:12px;" />`
    : '<h2 style="color:#B8A830;font-family:Georgia,serif;font-size:22px;margin:0;letter-spacing:2px;">HOUSE OF SAMPLE</h2>';

  const html = `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#f5f5f5;font-family:Arial,sans-serif;">
<div style="max-width:560px;margin:24px auto;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fff;">
  <div style="background:#1a1a1a;padding:20px 28px;text-align:center;">${logo}</div>
  <div style="padding:28px;">
    <h3 style="color:#1a1a1a;font-size:17px;margin:0 0 16px;">${header}</h3>
    <div style="background:#fffbeb;border:2px solid #B8A830;border-radius:8px;padding:14px 20px;text-align:center;margin-bottom:20px;">
      <p style="color:#6b7280;font-size:11px;letter-spacing:2px;text-transform:uppercase;margin:0 0 4px;">Inquiry Reference</p>
      <p style="color:#B8A830;font-size:22px;font-weight:bold;font-family:monospace;margin:0;letter-spacing:4px;">${vars.inquiryNumber}</p>
    </div>
    <div style="color:#374151;font-size:14px;line-height:1.8;margin-bottom:20px;">${body}</div>
    <div style="background:#f9fafb;border-radius:8px;padding:14px 18px;font-size:13px;color:#4b5563;">
      ${tmpl.emailVisitUrl   ? `<p style="margin:3px 0;">🌐 <a href="${tmpl.emailVisitUrl}"   style="color:#B8A830;">${tmpl.emailVisitUrl}</a></p>` : ''}
      ${tmpl.emailWriteEmail ? `<p style="margin:3px 0;">✉️ <a href="mailto:${tmpl.emailWriteEmail}" style="color:#B8A830;">${tmpl.emailWriteEmail}</a></p>` : ''}
      ${tmpl.emailReachPhone ? `<p style="margin:3px 0;">📞 <a href="tel:${tmpl.emailReachPhone}" style="color:#B8A830;">${tmpl.emailReachPhone}</a></p>` : ''}
    </div>
  </div>
  <div style="background:#f9fafb;border-top:1px solid #e5e7eb;padding:14px 28px;text-align:center;">
    <p style="color:#9ca3af;font-size:11px;margin:0;">© ${new Date().getFullYear()} House of Sample · All rights reserved</p>
  </div>
</div></body></html>`;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200">
        <div className="flex gap-1.5">
          <span className="w-3 h-3 rounded-full bg-red-400" />
          <span className="w-3 h-3 rounded-full bg-yellow-400" />
          <span className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <span className="text-xs text-gray-400 ml-2 font-mono">email preview</span>
      </div>
      <iframe
        srcDoc={html} title="Email Preview"
        className="w-full border-0 bg-gray-50"
        style={{ height: 460 }}
        sandbox="allow-same-origin"
      />
    </div>
  );
}

// ── SMS Preview ────────────────────────────────────────────────────────────────
function SmsPreview({ tmpl, vars }) {
  const lines = [
    interpolate(tmpl.smsHeader, vars),
    interpolate(tmpl.smsBody, vars),
    tmpl.smsVisitUrl   ? `Visit us at: ${tmpl.smsVisitUrl}`   : '',
    tmpl.smsWriteEmail ? `Write us at: ${tmpl.smsWriteEmail}` : '',
    tmpl.smsReachPhone ? `Reach us at: ${tmpl.smsReachPhone}` : '',
  ].filter(Boolean).join('\n');

  return (
    <div className="bg-gray-900 rounded-2xl p-4 shadow-xl max-w-xs mx-auto">
      <div className="bg-gray-800 rounded-t-xl px-3 py-1.5 text-center">
        <span className="text-gray-400 text-xs">House of Sample</span>
      </div>
      <div className="bg-gray-700 rounded-b-xl rounded-tr-xl px-4 py-3 mt-0.5 shadow-inner">
        <pre className="text-white text-xs leading-relaxed font-sans whitespace-pre-wrap">{lines}</pre>
      </div>
      <div className="text-center mt-2">
        <span className="text-gray-500 text-xs">{lines.length} characters</span>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function AdminAutoReplyMail() {
  const { isMasterAdmin } = useAuth();
  const [tab, setTab]           = useState('email');
  const [tmpl, setTmpl]         = useState(DEFAULT_TMPL);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied]     = useState(false);

  // Phone verification settings
  const [phoneVerifEnabled, setPhoneVerifEnabled] = useState(() => {
    try { return localStorage.getItem('phoneVerifEnabled') === 'true'; } catch { return false; }
  });
  const [fast2smsKey, setFast2smsKey]     = useState('');
  const [showApiKey, setShowApiKey]       = useState(false);
  const [savingPhoneVerif, setSavingPhoneVerif] = useState(false);

  // Load template on mount
  useEffect(() => {
    if (!isMasterAdmin) { setLoading(false); return; }
    api.get('/contact/templates')
      .then(r => { if (r.data.template) setTmpl(r.data.template); })
      .catch(() => toast.error('Failed to load templates'))
      .finally(() => setLoading(false));
  }, [isMasterAdmin]);

  const t = (key) => (val) => setTmpl(prev => ({ ...prev, [key]: typeof val === 'string' ? val : val.target.value }));

  const handleSave = async () => {
    if (!isMasterAdmin) return;
    setSaving(true);
    try {
      const res = await api.put('/contact/templates', tmpl);
      setTmpl(res.data.template);
      toast.success('Templates saved successfully');
    } catch { toast.error('Failed to save templates'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async () => {
    if (!logoFile || !isMasterAdmin) return;
    setUploading(true);
    const fd = new FormData(); fd.append('logo', logoFile);
    try {
      const res = await api.post('/contact/templates/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTmpl(prev => ({ ...prev, emailLogoUrl: res.data.logoUrl }));
      setLogoFile(null);
      toast.success('Logo uploaded');
    } catch { toast.error('Logo upload failed'); }
    finally { setUploading(false); }
  };

  const copyEmailHtml = () => {
    const dummy = document.createElement('textarea');
    dummy.value = `Subject: ${interpolate(tmpl.emailHeader, SAMPLE_VARS)}\n\n${interpolate(tmpl.emailBody, SAMPLE_VARS)}`;
    document.body.appendChild(dummy); dummy.select(); document.execCommand('copy'); document.body.removeChild(dummy);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    toast.info('Template copied to clipboard');
  };

  const handleSavePhoneVerif = async () => {
    if (!isMasterAdmin) return;
    setSavingPhoneVerif(true);
    try {
      // Persist toggle to localStorage
      localStorage.setItem('phoneVerifEnabled', String(phoneVerifEnabled));
      // Optionally persist API key to site_settings
      if (fast2smsKey) {
        await api.put('/settings', { fast2smsApiKey: fast2smsKey });
      }
      toast.success('Phone verification settings saved');
    } catch {
      // If API call fails, at least localStorage is saved
      localStorage.setItem('phoneVerifEnabled', String(phoneVerifEnabled));
      toast.success('Phone verification settings saved (locally)');
    } finally {
      setSavingPhoneVerif(false);
    }
  };

  const TABS = [
    { id: 'email', icon: Mail,         label: 'Email Template',  color: '#B8A830' },
    { id: 'sms',   icon: MessageSquare,label: 'SMS Template',    color: '#4F8EF7' },
    { id: 'brand', icon: Image,        label: 'Branding',        color: '#10B981', masterOnly: true },
  ].filter(tab => !tab.masterOnly || isMasterAdmin);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="text-amber-500 animate-spin" />
        <span className="ml-3 text-gray-500 text-sm">Loading templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
              <Zap size={18} className="text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Auto Reply Mail</h1>
          </div>
          <p className="text-sm text-gray-400 pl-12">Manage inquiry confirmation email &amp; SMS templates</p>
        </div>
        <div className="flex items-center gap-3 pl-12 sm:pl-0">
          <RoleBadge isMasterAdmin={isMasterAdmin} />
          {isMasterAdmin && (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm disabled:opacity-50">
              <Save size={14} /> {saving ? 'Saving...' : 'Save All'}
            </button>
          )}
        </div>
      </div>

      {/* Permission notice for non-master */}
      {!isMasterAdmin && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <AlertCircle size={16} className="text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-700">View Only Access</p>
            <p className="text-xs text-blue-600 mt-0.5">You can preview templates. Contact your Master Admin to make changes.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-100 pb-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-xl border-b-2 transition-colors
              ${tab === t.id ? 'border-amber-500 text-amber-700 bg-amber-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
            <t.icon size={14} /> {t.label}
            {t.id === 'brand' && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full ml-1">Master</span>}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 pb-1">
          <button onClick={() => setShowPreview(p => !p)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-xl border transition-colors
              ${showPreview ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
            {showPreview ? <EyeOff size={12} /> : <Eye size={12} />}
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      {/* ── EMAIL TAB ────────────────────────────────────────────────────── */}
      {tab === 'email' && (
        <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1 max-w-2xl'}`}>
          {/* Form */}
          <div className="space-y-5">
            <SectionCard title="Inquiry Confirmation Email" icon={Mail} iconColor="#B8A830">
              <Field label="Email Subject / Header" hint="Shown as the email subject line. Use {{inquiryNumber}} and {{name}}.">
                <TextArea value={tmpl.emailHeader} onChange={t('emailHeader')} rows={2} onInsert={() => {}} disabled={!isMasterAdmin} />
              </Field>
              <Field label="Email Body" hint="Main message body. Use variables to personalise.">
                <TextArea value={tmpl.emailBody} onChange={t('emailBody')} rows={6} onInsert={() => {}} disabled={!isMasterAdmin} />
              </Field>
            </SectionCard>

            <SectionCard title="Contact Details in Footer" icon={ExternalLink} iconColor="#6B7280" collapsible>
              <Field label="Website URL">
                <TextInput value={tmpl.emailVisitUrl} onChange={t('emailVisitUrl')} placeholder="https://www.houseofsample.in" disabled={!isMasterAdmin} />
              </Field>
              <Field label="Reply-To Email">
                <TextInput value={tmpl.emailWriteEmail} onChange={t('emailWriteEmail')} placeholder="sales@houseofsample.in" disabled={!isMasterAdmin} />
              </Field>
              <Field label="Phone Number">
                <TextInput value={tmpl.emailReachPhone} onChange={t('emailReachPhone')} placeholder="+91 85114 28123" disabled={!isMasterAdmin} />
              </Field>
            </SectionCard>

            {/* Variable reference */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Available Variables</p>
              <div className="flex flex-wrap gap-2">
                {VARIABLES.map(v => (
                  <code key={v} className="text-xs bg-white border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full font-mono">
                    {`{{${v}}}`}
                  </code>
                ))}
              </div>
            </div>

            {isMasterAdmin && (
              <div className="flex gap-3">
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
                  <Save size={14} /> {saving ? 'Saving...' : 'Save Email Template'}
                </button>
                <button onClick={copyEmailHtml}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm rounded-xl transition-colors">
                  {copied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>

          {/* Live Preview */}
          {showPreview && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live Email Preview</p>
                <span className="text-xs text-gray-400">Using sample data</span>
              </div>
              <EmailPreview tmpl={tmpl} vars={SAMPLE_VARS} />
            </div>
          )}
        </div>
      )}

      {/* ── SMS TAB ──────────────────────────────────────────────────────── */}
      {tab === 'sms' && (
        <div className={`grid gap-6 ${showPreview ? 'lg:grid-cols-2' : 'grid-cols-1 max-w-2xl'}`}>
          {/* Form */}
          <div className="space-y-5">
            <SectionCard title="Inquiry Confirmation SMS" icon={MessageSquare} iconColor="#4F8EF7">
              <Field label="Message Header" hint="First line — include {{name}} and {{inquiryNumber}}.">
                <TextArea value={tmpl.smsHeader} onChange={t('smsHeader')} rows={2} onInsert={() => {}} disabled={!isMasterAdmin} />
              </Field>
              <Field label="Message Body" hint="Second line — follow-up message.">
                <TextArea value={tmpl.smsBody} onChange={t('smsBody')} rows={3} onInsert={() => {}} disabled={!isMasterAdmin} />
              </Field>
            </SectionCard>

            <SectionCard title="Contact Info in SMS" icon={ExternalLink} iconColor="#6B7280" collapsible>
              <Field label="Website URL">
                <TextInput value={tmpl.smsVisitUrl} onChange={t('smsVisitUrl')} placeholder="https://www.houseofsample.in" disabled={!isMasterAdmin} />
              </Field>
              <Field label="Email Address">
                <TextInput value={tmpl.smsWriteEmail} onChange={t('smsWriteEmail')} placeholder="sales@houseofsample.in" disabled={!isMasterAdmin} />
              </Field>
              <Field label="Phone Number">
                <TextInput value={tmpl.smsReachPhone} onChange={t('smsReachPhone')} placeholder="+91 85114 28123" disabled={!isMasterAdmin} />
              </Field>
            </SectionCard>

            {isMasterAdmin && (
              <button onClick={handleSave} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
                <Save size={14} /> {saving ? 'Saving...' : 'Save SMS Template'}
              </button>
            )}
          </div>

          {/* SMS Preview */}
          {showPreview && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live SMS Preview</p>
                <span className="text-xs text-gray-400">Using sample data</span>
              </div>
              <SmsPreview tmpl={tmpl} vars={SAMPLE_VARS} />
            </div>
          )}
        </div>
      )}

          {/* ── Phone Verification Settings (SMS tab, below SMS template) ── */}
          {tab === 'sms' && isMasterAdmin && (
            <div className="max-w-2xl mt-6">
              <SectionCard title="Phone Verification Settings" icon={Phone} iconColor="#4F8EF7">
                {/* Toggle */}
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Require Phone OTP Verification</p>
                    <p className="text-xs text-gray-400 mt-0.5">When enabled, customers must verify their phone number via SMS OTP.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPhoneVerifEnabled(v => !v)}
                    className="flex items-center gap-2 focus:outline-none"
                    aria-pressed={phoneVerifEnabled}
                    aria-label="Toggle phone OTP verification"
                  >
                    {phoneVerifEnabled
                      ? <ToggleRight size={36} className="text-blue-500" />
                      : <ToggleLeft  size={36} className="text-gray-400" />}
                    <span className={`text-xs font-semibold ${phoneVerifEnabled ? 'text-blue-600' : 'text-gray-400'}`}>
                      {phoneVerifEnabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                </div>

                {/* Status message */}
                {!phoneVerifEnabled ? (
                  <div className="flex items-start gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    <AlertCircle size={14} className="text-gray-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-gray-500">
                      Phone verification is currently <strong>disabled</strong>. Customers only need to verify their email.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
                      <CheckCircle size={14} className="text-blue-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-blue-600">
                        When enabled, customers must verify their phone number via SMS OTP before submitting an inquiry.
                      </p>
                    </div>
                    {/* Fast2SMS API Key */}
                    <Field label="Fast2SMS API Key" hint="Enter your Fast2SMS API key to send SMS OTPs.">
                      <div className="flex gap-2">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={fast2smsKey}
                          onChange={e => setFast2smsKey(e.target.value)}
                          placeholder="Enter Fast2SMS API Key…"
                          className={inputCls}
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(v => !v)}
                          className="px-3 py-2 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl text-gray-500 transition-colors"
                          aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                        >
                          {showApiKey ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Leave blank to keep the existing key. The key is stored securely on the server.
                      </p>
                    </Field>
                  </div>
                )}

                <button
                  onClick={handleSavePhoneVerif}
                  disabled={savingPhoneVerif}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  <Save size={14} /> {savingPhoneVerif ? 'Saving…' : 'Save Phone Verification Settings'}
                </button>
              </SectionCard>
            </div>
          )}

      {/* ── BRAND TAB (Master Admin only) ────────────────────────────────── */}
      {tab === 'brand' && isMasterAdmin && (
        <div className="max-w-2xl space-y-6">
          <SectionCard title="Brand Logo" icon={Image} iconColor="#10B981">
            {/* Current logo */}
            <div className="bg-gray-900 rounded-xl p-6 flex items-center justify-center min-h-28">
              {tmpl.emailLogoUrl ? (
                <img
                  src={resolveImage(tmpl.emailLogoUrl)}
                  alt="Brand logo"
                  className="max-h-16 max-w-xs object-contain"
                  onError={e => { e.target.style.display='none'; }}
                />
              ) : (
                <div className="text-center">
                  <Image size={32} className="text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500 text-xs">No logo uploaded</p>
                </div>
              )}
            </div>

            {/* Upload */}
            <div className="space-y-3">
              <Field label="Upload New Logo" hint="Recommended: PNG with transparent background, min 300×100px.">
                <div className="flex gap-3">
                  <label className="flex-1 flex items-center gap-2 px-3 py-2.5 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-amber-400 transition-colors bg-gray-50">
                    <Upload size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-500">{logoFile ? logoFile.name : 'Choose image file…'}</span>
                    <input type="file" accept="image/*" className="hidden"
                      onChange={e => setLogoFile(e.target.files[0])} />
                  </label>
                  <button onClick={handleLogoUpload} disabled={!logoFile || uploading}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 shrink-0">
                    <Upload size={14} /> {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </Field>
              {logoFile && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  <CheckCircle size={12} className="text-green-500" />
                  Selected: <span className="font-medium">{logoFile.name}</span>
                  <span className="text-gray-400">({(logoFile.size/1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>

            {/* Logo URL manual override */}
            <Field label="Or enter Logo URL directly">
              <TextInput value={tmpl.emailLogoUrl} onChange={t('emailLogoUrl')} placeholder="https://... or /uploads/logo.png" />
            </Field>

            <button onClick={handleSave} disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
              <Save size={14} /> {saving ? 'Saving…' : 'Save Logo Settings'}
            </button>
          </SectionCard>

          {/* Brand info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Crown size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-1">Master Admin Feature</p>
                <p className="text-xs text-amber-600 leading-relaxed">
                  The brand logo appears in the header of all auto-reply emails sent to customers.
                  It is referenced via the <code className="font-mono bg-amber-100 px-1 rounded">{'{{brand_logo}}'}</code> variable.
                </p>
              </div>
            </div>
          </div>

          {/* Fast2SMS API Key display */}
          <SectionCard title="Fast2SMS API Key" icon={Key} iconColor="#6366F1">
            <Field label="FAST2SMS_API_KEY" hint="Stored in the server .env file. Shown masked for reference only.">
              <div className="flex gap-2 items-center">
                <input
                  type="password"
                  readOnly
                  value="••••••••••••••••••••••••••••••••"
                  className={`${inputCls} flex-1 cursor-not-allowed bg-gray-50 text-gray-400`}
                  aria-label="Masked Fast2SMS API key"
                />
                <span className="text-xs text-gray-400 shrink-0">masked</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                To update this key, edit <code className="font-mono bg-gray-100 px-1 rounded">.env</code> on the server or use the Phone Verification Settings in the SMS tab.
              </p>
            </Field>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
